import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { AuthContext } from '../../common/authContext';
import { individualRealtorSignup } from '../../services/account.service';
import { Account } from '../../models';
import { getTenantPublic } from '../../services/tenant.service';
import { Alert, Skeleton } from '@material-ui/lab';
import { Snackbar } from '@material-ui/core';
import RealtorSignUpScreen from '../../components/User/RealtorSignUpScreen/RealtorSignUpScreen';

export default function RealtorSignUpScreenContainer({
  history
}: {
  history: History;
}) {
  const [error, setError] = useState<{ message?: string }>({});
  const [tenantName, setTenantName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const { authUser } = useContext(AuthContext);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    const slug = process.env.REACT_APP_REALTOR_SLUG ?? 'real-genius-realtor';
    if (!slug) {
      setError({ message: 'Realtor sign-up page is not configured' });
      return;
    }
    const tenantRequest = async () => {
      try {
        const tenant = await getTenantPublic(slug);
        setTenantName(tenant.name ?? '');
        setTenantId(tenant._id);
      } catch (err) {
        setError({ message: 'Error getting tenant information' });
      }
    };
    tenantRequest();
  }, []);

  const handleSignUp = useCallback(
    async ({
      email,
      password,
      name,
      company,
      lendersAndTitleCompaniesFeedback,
      marketAreaZipcode
    }: {
      email: string;
      password: string;
      name: string;
      company: string;
      tenant: string;
      lendersAndTitleCompaniesFeedback: string;
      marketAreaZipcode?: string;
    }) => {
      const [firstName, lastName = ''] = name.split(' ');
      const account: Partial<Account> = {
        firstName,
        lastName,
        companyName: company,
        email,
        password,
        tenants: [tenantId],
        lendersAndTitleCompaniesFeedback,
        marketAreaZipcode
      };
      try {
        const accountCreated = await individualRealtorSignup(account);
        if (accountCreated?._id) {
          setAccountCreated(true);
        }
      } catch (err) {
        throw err instanceof Error ? err.message : String(err);
      }
    },
    [history, tenantId]
  );

  if (authUser?._id) {
    return <Redirect to="/welcome" />;
  }

  if (!tenantName && !error?.message) {
    return <Skeleton />;
  }

  if (error?.message) {
    return (
      <Snackbar
        open={true}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          {error.message}
        </Alert>
      </Snackbar>
    );
  }

  return (
    <RealtorSignUpScreen
      onSignUp={handleSignUp}
      // error={creationError}
      tenantName={tenantName}
      accountCreated={accountCreated}
    />
  );
}

RealtorSignUpScreenContainer.propTypes = {
  history: PropTypes.object.isRequired
};
